// src/pages/BIDashboardList.tsx — Редизайн 2026 (top-navbar + full-width)
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Eye, Edit2, Trash2, LayoutDashboard, Brain, Search,
  Layers, Sparkles, BarChart3, TrendingUp,
} from 'lucide-react';
import { useConfirm } from '../contexts/ConfirmContext';
import { useToast } from '../contexts/ToastContext';
import { getDashboards, deleteDashboard, BIDashboard } from '../api/bi';
import { getMindMaps, deleteMindMap, MindMap } from '../api/mindmaps';
import BILayout from '../components/BILayout';
import Loader from '../components/Loader';
import { BarChart, Bar, PieChart, Pie, ResponsiveContainer } from 'recharts';

const BIDashboardList: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [dashboards, setDashboards] = useState<BIDashboard[]>([]);
  const [mindMaps, setMindMaps] = useState<MindMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [dashRes, mindRes] = await Promise.all([getDashboards(), getMindMaps()]);
      setDashboards(dashRes.data);
      setMindMaps(mindRes.data);
    } catch (err) {
      console.error('Ошибка загрузки данных', err);
      showToast('Не удалось загрузить дашборды', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDashboard = (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    confirm.showConfirm({
      title: 'Удаление дашборда',
      message: `Удалить дашборд "${name}"?`,
      onConfirm: async () => {
        try { await deleteDashboard(id); await loadAll(); showToast('Дашборд удалён', 'success'); }
        catch { showToast('Не удалось удалить дашборд', 'error'); }
      },
    });
  };

  const handleDeleteMindMap = (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    confirm.showConfirm({
      title: 'Удаление карты',
      message: `Удалить карту "${name}"?`,
      onConfirm: async () => {
        try { await deleteMindMap(id); await loadAll(); showToast('Карта удалена', 'success'); }
        catch { showToast('Не удалось удалить карту', 'error'); }
      },
    });
  };

  const renderPreview = (dashboard: BIDashboard) => {
    const previewWidgets = dashboard.widgets.slice(0, 2);
    return (
      <div style={styles.previewContainer}>
        {previewWidgets.map(widget => {
          const data = widget.userData && widget.userData.length ? widget.userData : [{ name: 'Пример', value: 42 }];
          if (widget.type === 'kpi') {
            return (
              <div key={widget.id} style={styles.previewKpi}>
                <span style={styles.previewKpiTitle}>{widget.title}</span>
                <span style={styles.previewKpiValue}>{data[0]?.value || 0}</span>
              </div>
            );
          } else if (widget.type === 'bar') {
            return (
              <div key={widget.id} style={styles.previewChart}>
                <ResponsiveContainer width="100%" height={60}>
                  <BarChart data={data.slice(0, 3)}>
                    <Bar dataKey="value" fill="url(#barGradient)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          } else if (widget.type === 'pie') {
            return (
              <div key={widget.id} style={styles.previewChart}>
                <ResponsiveContainer width="100%" height={60}>
                  <PieChart>
                    <Pie data={data.slice(0, 3)} dataKey="value" innerRadius={14} outerRadius={24}
                      fill="#6a11cb" stroke="rgba(255,255,255,0.5)" strokeWidth={1} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            );
          }
          return null;
        })}
        {previewWidgets.length === 0 && (
          <div style={styles.previewEmpty}>
            <BarChart3 size={24} color="#c4c8e0" />
            <span style={styles.previewEmptyText}>Пустой дашборд</span>
          </div>
        )}
      </div>
    );
  };

  const filteredDashboards = useMemo(() => dashboards.filter(db =>
    db.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (db.description && db.description.toLowerCase().includes(searchTerm.toLowerCase()))
  ), [dashboards, searchTerm]);

  const filteredMindMaps = useMemo(() => mindMaps.filter(map =>
    map.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (map.description && map.description.toLowerCase().includes(searchTerm.toLowerCase()))
  ), [mindMaps, searchTerm]);

  const totalWidgets = useMemo(
    () => dashboards.reduce((acc, d) => acc + (d.widgets?.length || 0), 0),
    [dashboards]
  );

  if (loading) return <BILayout><Loader /></BILayout>;

  return (
    <BILayout>
      {/* ─── HERO ─── */}
      <div style={styles.hero}>
        <div style={styles.heroLeft}>
          <div style={styles.heroIconWrap}>
            <div style={styles.heroIconBg} />
            <div style={styles.heroIconContent}><span style={styles.heroIconEmoji}>📊</span></div>
          </div>
          <div>
            <h1 style={styles.title}>BI Аналитика</h1>
            <p style={styles.subtitle}>
              Создавайте дашборды, визуализации и mind-карты. Работайте с данными в одном пространстве.
            </p>
          </div>
        </div>

        <div style={styles.heroActions}>
          <button
            onClick={() => navigate('/app/bi/builder/new')}
            style={styles.primaryBtn}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 26px rgba(102,126,234,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(102,126,234,0.25)'; }}
          >
            <Plus size={16} /> <span>Новый дашборд</span>
          </button>
          <button
            onClick={() => navigate('/app/bi/mindmaps/new')}
            style={styles.greenBtn}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 26px rgba(16,185,129,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(16,185,129,0.25)'; }}
          >
            <Brain size={16} /> <span>Mind-карта</span>
          </button>
        </div>
      </div>

      {/* ─── STATS ─── */}
      <div style={styles.statsGrid}>
        <StatCard icon={LayoutDashboard} label="Дашборды" value={dashboards.length} gradient="linear-gradient(135deg, #6a11cb, #2575fc)" />
        <StatCard icon={Brain} label="Mind-карты" value={mindMaps.length} gradient="linear-gradient(135deg, #8b5cf6, #6a11cb)" />
        <StatCard icon={BarChart3} label="Виджетов" value={totalWidgets} gradient="linear-gradient(135deg, #10b981, #059669)" />
        <StatCard icon={TrendingUp} label="Всего объектов" value={dashboards.length + mindMaps.length} gradient="linear-gradient(135deg, #f59e0b, #d97706)" />
      </div>

      {/* ─── SEARCH ─── */}
      <div style={styles.searchWrapper}>
        <Search style={styles.searchIcon} size={18} />
        <input
          type="text"
          placeholder="Поиск дашбордов и mind-карт..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
          onFocus={(e) => { e.currentTarget.style.borderColor = '#667eea'; e.currentTarget.style.boxShadow = '0 0 0 4px rgba(102,126,234,0.12)'; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(31,38,135,0.06)'; }}
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} style={styles.clearSearch}>×</button>
        )}
      </div>

      {/* ─── ДАШБОРДЫ ─── */}
      <div id="bi-dashboards" style={{ scrollMarginTop: 100 }}>
        <SectionHeader
          icon={<LayoutDashboard size={16} color="#fff" />}
          gradient="linear-gradient(135deg, #6a11cb, #2575fc)"
          title="Дашборды"
          subtitle={`${filteredDashboards.length} из ${dashboards.length}`}
        />
      </div>

      <div style={styles.grid}>
        {filteredDashboards.length === 0 && (
          <EmptyState
            icon={<BarChart3 size={40} color="#c4c8e0" />}
            text={searchTerm ? `Ничего не найдено по «${searchTerm}»` : 'Нет дашбордов. Создайте первый!'}
            action={!searchTerm ? { label: 'Создать дашборд', onClick: () => navigate('/app/bi/builder/new') } : undefined}
          />
        )}
        {filteredDashboards.map(db => (
          <div
            key={db._id}
            className="bi-card"
            style={styles.card}
            onClick={() => navigate(`/app/bi/viewer/${db._id}`)}
          >
            <div style={styles.cardHeader}>
              <div style={styles.cardIconBlue}>
                <LayoutDashboard size={20} color="#fff" />
              </div>
              <div style={styles.cardTitleWrap}>
                <h3 style={styles.cardTitle}>{db.name}</h3>
                <div style={styles.cardMeta}>
                  <Sparkles size={11} color="#667eea" />
                  <span>{db.widgets?.length || 0} виджетов</span>
                </div>
              </div>
            </div>
            <p style={styles.cardDesc}>{db.description || 'Без описания'}</p>
            {renderPreview(db)}
            <div style={styles.cardActions} onClick={e => e.stopPropagation()}>
              <button onClick={() => navigate(`/app/bi/viewer/${db._id}`)} style={styles.iconButton} title="Просмотр">
                <Eye size={15} />
              </button>
              <button onClick={() => navigate(`/app/bi/builder/${db._id}`)} style={styles.iconButton} title="Редактировать">
                <Edit2 size={15} />
              </button>
              <button onClick={(e) => handleDeleteDashboard(db._id, db.name, e)} style={{ ...styles.iconButton, ...styles.iconButtonDanger }} title="Удалить">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ─── MIND-КАРТЫ ─── */}
      <div id="bi-mindmaps" style={{ scrollMarginTop: 100 }}>
        <SectionHeader
          icon={<Brain size={16} color="#fff" />}
          gradient="linear-gradient(135deg, #8b5cf6, #6a11cb)"
          title="Mind-карты"
          subtitle={`${filteredMindMaps.length} из ${mindMaps.length}`}
          marginTop={32}
        />
      </div>

      <div style={styles.grid}>
        {filteredMindMaps.length === 0 && (
          <EmptyState
            icon={<Brain size={40} color="#c4c8e0" />}
            text={searchTerm ? `Ничего не найдено по «${searchTerm}»` : 'Нет mind-карт. Создайте первую!'}
            action={!searchTerm ? { label: 'Создать mind-карту', onClick: () => navigate('/app/bi/mindmaps/new') } : undefined}
          />
        )}
        {filteredMindMaps.map(map => (
          <div
            key={map._id}
            className="bi-card"
            style={styles.card}
            onClick={() => navigate(`/app/bi/mindmaps/${map._id}`)}
          >
            <div style={styles.cardHeader}>
              <div style={styles.cardIconPurple}>
                <Brain size={20} color="#fff" />
              </div>
              <div style={styles.cardTitleWrap}>
                <h3 style={styles.cardTitle}>{map.name}</h3>
                <div style={styles.cardMeta}>
                  <Sparkles size={11} color="#667eea" />
                  <span>Mind-карта</span>
                </div>
              </div>
            </div>
            <p style={styles.cardDesc}>{map.description || 'Без описания'}</p>
            <div style={styles.previewContainer}>
              <div style={styles.previewEmpty}>
                <Brain size={24} color="#c4c8e0" />
                <span style={styles.previewEmptyText}>Mind-карта</span>
              </div>
            </div>
            <div style={styles.cardActions} onClick={e => e.stopPropagation()}>
              <button onClick={() => navigate(`/app/bi/mindmaps/${map._id}`)} style={styles.iconButton} title="Открыть">
                <Edit2 size={15} />
              </button>
              <button onClick={(e) => handleDeleteMindMap(map._id, map.name, e)} style={{ ...styles.iconButton, ...styles.iconButtonDanger }} title="Удалить">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Градиент для мини-баров */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(106, 17, 203, 0.9)" />
            <stop offset="100%" stopColor="rgba(37, 117, 252, 0.6)" />
          </linearGradient>
        </defs>
      </svg>

      <style>{`
        @keyframes bi-fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .bi-card { animation: bi-fadeUp 0.5s ease-out backwards; }
        .bi-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 24px 48px rgba(31,38,135,0.14), 0 8px 16px rgba(31,38,135,0.06) !important;
          border-color: rgba(102,126,234,0.3) !important;
        }
        @media (max-width: 1400px) {
          .bi-grid { grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)) !important; }
        }
        @media (max-width: 700px) {
          .bi-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </BILayout>
  );
};

// ─── Компоненты ───
const StatCard: React.FC<{ icon: any; label: string; value: number; gradient: string }> = ({ icon: IconC, label, value, gradient }) => (
  <div style={styles.statCard}>
    <div style={{ ...styles.statIcon, background: gradient }}>
      <IconC size={18} color="#fff" />
    </div>
    <div>
      <div style={styles.statValue}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  </div>
);

const SectionHeader: React.FC<{ icon: React.ReactNode; gradient: string; title: string; subtitle: string; marginTop?: number }> = ({ icon, gradient, title, subtitle, marginTop }) => (
  <div style={{ ...styles.sectionHeader, marginTop: marginTop || 0 }}>
    <div style={{ ...styles.sectionIcon, background: gradient }}>{icon}</div>
    <div>
      <div style={styles.sectionTitle}>{title}</div>
      <div style={styles.sectionSub}>{subtitle}</div>
    </div>
  </div>
);

const EmptyState: React.FC<{ icon: React.ReactNode; text: string; action?: { label: string; onClick: () => void } }> = ({ icon, text, action }) => (
  <div style={styles.empty}>
    {icon}
    <p style={styles.emptyText}>{text}</p>
    {action && (
      <button onClick={action.onClick} style={styles.emptyBtn}>
        <Plus size={14} /> <span>{action.label}</span>
      </button>
    )}
  </div>
);

// ═══════════ СТИЛИ ═══════════
const styles: Record<string, React.CSSProperties> = {
  hero: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 24, flexWrap: 'wrap' },
  heroLeft: { display: 'flex', alignItems: 'center', gap: 20, flex: 1, minWidth: 300 },
  heroIconWrap: { position: 'relative', width: 64, height: 64, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  heroIconBg: { position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6a11cb, #2575fc, #00c6ff)', borderRadius: 20, boxShadow: '0 14px 32px rgba(102,126,234,0.32)' },
  heroIconContent: { position: 'relative', display: 'flex', alignItems: 'center', zIndex: 1 },
  heroIconEmoji: { fontSize: 32, lineHeight: 1 },
  title: { fontSize: 28, fontWeight: 800, margin: '0 0 4px', background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: -0.5, lineHeight: 1.15 },
  subtitle: { fontSize: 14, color: '#5a5f7a', margin: 0, maxWidth: 720, lineHeight: 1.5 },
  heroActions: { display: 'flex', gap: 10, flexWrap: 'wrap' },

  primaryBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 20px rgba(102,126,234,0.25)', transition: 'all 0.2s', fontFamily: 'inherit' },
  greenBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 20px rgba(16,185,129,0.25)', transition: 'all 0.2s', fontFamily: 'inherit' },

  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 24 },
  statCard: { display: 'flex', alignItems: 'center', gap: 14, padding: 18, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 18, boxShadow: '0 10px 28px rgba(31,38,135,0.06)' },
  statIcon: { width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(102,126,234,0.25)', flexShrink: 0 },
  statValue: { fontSize: 22, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.1 },
  statLabel: { fontSize: 12, color: '#8b8fa8', fontWeight: 600, marginTop: 2 },

  searchWrapper: { position: 'relative', marginBottom: 24, maxWidth: 520 },
  searchIcon: { position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', color: '#8b8fa8', pointerEvents: 'none' as any },
  searchInput: { width: '100%', padding: '13px 44px 13px 48px', borderRadius: 40, border: '1px solid rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', fontSize: 14, outline: 'none', fontFamily: 'inherit', color: '#1a1a2e', boxSizing: 'border-box' as any, boxShadow: '0 8px 24px rgba(31,38,135,0.06)', transition: 'all 0.2s' },
  clearSearch: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 28, height: 28, borderRadius: 8, background: 'rgba(102,126,234,0.1)', border: 'none', cursor: 'pointer', color: '#667eea', fontSize: 18, fontWeight: 800, lineHeight: 1, fontFamily: 'inherit' },

  sectionHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  sectionIcon: { width: 32, height: 32, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(102,126,234,0.25)', flexShrink: 0 },
  sectionTitle: { fontSize: 16, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.2 },
  sectionSub: { fontSize: 12, color: '#8b8fa8', marginTop: 2 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18, marginBottom: 24 },

  card: { position: 'relative', background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 22, padding: 20, boxShadow: '0 12px 32px rgba(31,38,135,0.08)', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', display: 'flex', flexDirection: 'column' },
  cardHeader: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 },
  cardIconBlue: { width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(102,126,234,0.25)', flexShrink: 0 },
  cardIconPurple: { width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #8b5cf6, #6a11cb)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 16px rgba(139,92,246,0.25)', flexShrink: 0 },
  cardTitleWrap: { flex: 1, minWidth: 0 },
  cardTitle: { fontSize: 15, fontWeight: 800, margin: 0, color: '#1a1a2e', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as any },
  cardMeta: { display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 11, color: '#667eea', fontWeight: 700 },
  cardDesc: { fontSize: 13, color: '#5a5f7a', marginBottom: 14, lineHeight: 1.5, minHeight: 38, overflow: 'hidden', display: '-webkit-box' as any, WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any },

  previewContainer: { display: 'flex', gap: 10, marginBottom: 14, background: 'rgba(102,126,234,0.06)', borderRadius: 14, padding: 8, justifyContent: 'space-around', minHeight: 76, border: '1px solid rgba(102,126,234,0.12)', alignItems: 'center' },
  previewKpi: { textAlign: 'center' as any, background: 'rgba(255,255,255,0.7)', padding: 8, borderRadius: 10, flex: 1 },
  previewKpiTitle: { fontSize: 10, color: '#8b8fa8', display: 'block', marginBottom: 4, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as any },
  previewKpiValue: { fontSize: 18, fontWeight: 800, color: '#2575fc' },
  previewChart: { flex: 1, height: 60 },
  previewEmpty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 4 },
  previewEmptyText: { fontSize: 11, color: '#8b8fa8', fontWeight: 600 },

  cardActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 'auto' },
  iconButton: { background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(102,126,234,0.15)', borderRadius: 10, padding: 7, cursor: 'pointer', color: '#5a5f7a', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' },
  iconButtonDanger: { color: '#dc2626' },

  empty: { gridColumn: '1 / -1' as any, textAlign: 'center' as any, padding: 48, background: 'rgba(255,255,255,0.5)', borderRadius: 22, border: '1px dashed rgba(102,126,234,0.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 },
  emptyText: { margin: 0, fontSize: 14, color: '#8b8fa8', fontWeight: 600 },
  emptyBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 20px rgba(102,126,234,0.25)', fontFamily: 'inherit' },
};

export default BIDashboardList;
